import * as React from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { getTransactionAttachmentsByMetadata } from 'store/vat/api';
import type { TransactionAttachment } from 'store/vat/models/transactionAttachments';

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.mjs`;

type Preview = {
  url: string;
  kind: 'image' | 'pdf';
};

const PREVIEW_CACHE_LIMIT = 24;
const previewCache = new Map<string, Preview>();

const rememberPreview = (cacheKey: string, next: Preview) => {
  const previous = previewCache.get(cacheKey);
  if (previous && previous.url !== next.url) {
    URL.revokeObjectURL(previous.url);
  }
  previewCache.delete(cacheKey);
  previewCache.set(cacheKey, next);
  while (previewCache.size > PREVIEW_CACHE_LIMIT) {
    const oldest = previewCache.keys().next().value as string | undefined;
    if (!oldest) break;
    const dropped = previewCache.get(oldest);
    previewCache.delete(oldest);
    if (dropped) URL.revokeObjectURL(dropped.url);
  }
};

const mimeFor = (fileName: string): { mime: string; kind: Preview['kind'] } | null => {
  const ext = fileName.split('.').pop()?.toLowerCase();
  if (ext === 'jpg' || ext === 'jpeg') return { mime: 'image/jpeg', kind: 'image' };
  if (ext === 'png') return { mime: 'image/png', kind: 'image' };
  if (ext === 'gif') return { mime: 'image/gif', kind: 'image' };
  if (ext === 'webp') return { mime: 'image/webp', kind: 'image' };
  if (ext === 'pdf') return { mime: 'application/pdf', kind: 'pdf' };
  return null;
};

const toObjectUrl = (file: TransactionAttachment): Preview | null => {
  const typed = mimeFor(file.fileName || '');
  if (!typed || !file.fileContent) return null;
  const raw = file.fileContent.replace(/^data:[^;]+;base64,/, '');
  const binary = window.atob(raw);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  const url = URL.createObjectURL(new Blob([bytes], { type: typed.mime }));
  return { url, kind: typed.kind };
};

export const PwaReceiptThumb: React.FC<{
  connectionId?: string;
  metadataId?: string;
}> = ({ connectionId, metadataId }) => {
  const key = connectionId && metadataId ? `${connectionId}:${metadataId}` : '';
  const [preview, setPreview] = React.useState<Preview | null>(
    key && previewCache.has(key) ? previewCache.get(key) ?? null : null
  );
  const [visible, setVisible] = React.useState(false);
  const rootRef = React.useRef<HTMLSpanElement>(null);

  React.useEffect(() => {
    const node = rootRef.current;
    if (!node || typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return undefined;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) setVisible(true);
      },
      { rootMargin: '80px' }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  React.useEffect(() => {
    if (!visible || !connectionId || !metadataId) return undefined;
    const cacheKey = `${connectionId}:${metadataId}`;
    if (previewCache.has(cacheKey)) {
      setPreview(previewCache.get(cacheKey) ?? null);
      return undefined;
    }
    let cancelled = false;
    getTransactionAttachmentsByMetadata(connectionId, metadataId)
      .then((response) => {
        if (cancelled) return;
        const file = response.data?.files?.[0];
        const next = file ? toObjectUrl(file) : null;
        if (next) rememberPreview(cacheKey, next);
        setPreview(next);
      })
      .catch(() => {
        if (cancelled) return;
        setPreview(null);
      });
    return () => {
      cancelled = true;
    };
  }, [connectionId, metadataId, visible]);

  return (
    <span
      ref={rootRef}
      className={`pwa-thumb sm${preview ? ' has-preview' : ''}`}
      aria-hidden
    >
      {preview?.kind === 'image' ? (
        <img src={preview.url} alt="" />
      ) : null}
      {preview?.kind === 'pdf' ? (
        <Document file={preview.url} loading={null} error={null}>
          <Page
            pageNumber={1}
            width={80}
            renderTextLayer={false}
            renderAnnotationLayer={false}
          />
        </Document>
      ) : null}
    </span>
  );
};
